/**
 * TenantContext.jsx — Phase T1 tenant identity (single-tenant).
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

import { getBackendBase } from "@/lib/api";
const BACKEND_URL = getBackendBase();  // Phase APEX-C — same-origin only. See lib/api.js.
const STORAGE_KEY = "rp_tenant_cache_v1";
const TTL_MS = 5 * 60 * 1000; // 5 minutes

const TenantContext = createContext(null);

const FALLBACK = {
    display_name: "RentFlow",
    legal_name: "RentFlow",
    logo_url: null,
    address: null,
    website: null,
};

function readCache() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.savedAt || Date.now() - parsed.savedAt > TTL_MS) return null;
        return parsed.data || null;
    } catch {
        return null;
    }
}

function writeCache(data) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ savedAt: Date.now(), data }));
    } catch { /* localStorage full / private mode — ignore */ }
}

export function TenantProvider({ children }) {
    const [tenant, setTenant] = useState(() => readCache() || FALLBACK);
    const [loading, setLoading] = useState(true);

    const refreshTenant = useCallback(async () => {
        try {
            const { data } = await axios.get(`${BACKEND_URL}/api/public/tenant`);
            // Backend returns a relative logo URL so we can decide the host.
            const normalized = { ...data };
            if (normalized.logo_url && normalized.logo_url.startsWith("/")) {
                normalized.logo_url = `${BACKEND_URL}${normalized.logo_url}`;
            }
            setTenant(normalized);
            writeCache(normalized);
            return normalized;
        } catch {
            // Keep whatever we had (cache or fallback).
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshTenant();
    }, [refreshTenant]);

    return (
        <TenantContext.Provider value={{ tenant, loading, refreshTenant }}>
            {children}
        </TenantContext.Provider>
    );
}

export function useTenant() {
    const ctx = useContext(TenantContext);
    if (!ctx) return { tenant: FALLBACK, loading: false, refreshTenant: async () => null };
    return ctx;
}
